import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail, AlertCircle, Eye, EyeOff, Zap, Shield, BarChart3 } from "lucide-react";
import api from "@/lib/api";

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await api.login(email, password);
      const { access_token, refresh_token, user } = response.data;
      
      // Store tokens and user info
      api.setTokens(access_token, refresh_token);
      localStorage.setItem("wisedrive_user", JSON.stringify(user));
      localStorage.setItem("wisedrive_auth", "true");
      
      onLogin(user);
    } catch (err) {
      const message = err.response?.data?.detail || err.message || "Authentication failed";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen w-full overflow-hidden">
      {/* Left Panel - Brand Section with Dark Blue */}
      <div 
        className="relative hidden lg:flex flex-col justify-between overflow-hidden"
        style={{ backgroundColor: '#003399' }}
      >
        {/* Decorative gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#003399] via-[#002277] to-[#001155] opacity-90" />
        
        {/* Decorative shapes */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/5 rounded-full translate-y-1/3 -translate-x-1/3" />
        <div className="absolute top-1/2 left-1/4 w-48 h-48 bg-white/3 rounded-full" />
        
        {/* Top Content - Logo */}
        <div className="relative z-10 p-12">
          <img 
            src="/wisedrive-logo-new.png" 
            alt="Wisedrive" 
            className="h-14"
            data-testid="login-logo"
          />
        </div>
        
        {/* Center Content */}
        <div className="relative z-10 px-12 flex-1 flex flex-col justify-center">
          {/* Feature Pills */}
          <div className="flex flex-wrap gap-3 mb-10">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <Zap size={16} className="text-yellow-400" />
              <span className="text-sm font-medium text-white">AI-Powered Analysis</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <Shield size={16} className="text-green-400" />
              <span className="text-sm font-medium text-white">Verified Results</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <BarChart3 size={16} className="text-cyan-400" />
              <span className="text-sm font-medium text-white">Predictive Insights</span>
            </div>
          </div>
          
          {/* Headline */}
          <div className="space-y-5">
            <h1 className="text-4xl xl:text-5xl font-bold tracking-tight leading-tight text-white">
              Inspect Wise,
              <br />
              <span className="text-yellow-400">Drive Smart</span>
            </h1>
            <p className="text-lg text-white/80 max-w-md leading-relaxed">
              Advanced AI-powered sound and vision analysis for comprehensive vehicle health assessment. Trust our technology for accurate diagnostics.
            </p>
          </div>
        </div>
        
        {/* Bottom Content - Stats */}
        <div className="relative z-10 p-12">
          <div className="flex gap-12 pt-6 border-t border-white/20">
            <div>
              <div className="text-3xl font-bold text-yellow-400">98%</div>
              <div className="text-sm text-white/60">Accuracy Rate</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-yellow-400">50K+</div>
              <div className="text-sm text-white/60">Analyses Done</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-yellow-400">24/7</div>
              <div className="text-sm text-white/60">Available</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login/Register Form */}
      <div className="flex items-center justify-center bg-slate-50 p-6 sm:p-8 lg:p-12">
        <div className="w-full max-w-[420px] space-y-8 animate-fadeIn">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src="/wisedrive-logo-new.png" 
              alt="Wisedrive" 
              className="h-12 mx-auto"
            />
          </div>
          
          {/* Header */}
          <div className="space-y-3">
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight">
              Welcome back
            </h2>
            <p className="text-slate-500">
              Sign in to access your diagnostic dashboard
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Internal use only • Wisedrive employees
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {error && (
              <div 
                className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 animate-shake" 
                data-testid="login-error"
              >
                <AlertCircle size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">
                Email Address
              </Label>
              <div className="relative group">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#003399] transition-colors" />
                <Input
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-12 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-[#003399] focus:ring-2 focus:ring-[#003399]/20 rounded-xl transition-all duration-200"
                  data-testid="login-email"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">
                Password
              </Label>
              <div className="relative group">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#003399] transition-colors" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 pr-12 h-12 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-[#003399] focus:ring-2 focus:ring-[#003399]/20 rounded-xl transition-all duration-200"
                  data-testid="login-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-[#003399] hover:bg-[#002277] text-white font-semibold tracking-wide rounded-xl shadow-lg shadow-[#003399]/25 transition-all duration-200 hover:shadow-xl hover:shadow-[#003399]/30 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
              data-testid="login-submit"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-slate-50 text-slate-400">Secure authentication</span>
            </div>
          </div>

          {/* Footer Info */}
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-6 text-sm text-slate-400">
              <a href="#" className="hover:text-[#003399] transition-colors">Help Center</a>
              <span className="w-1 h-1 bg-slate-300 rounded-full" />
              <a href="#" className="hover:text-[#003399] transition-colors">Contact Support</a>
            </div>
            <p className="text-center text-xs text-slate-400">
              © 2026 Wisedrive Technologies. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out; }
        .animate-shake { animation: shake 0.5s ease-in-out; }
      `}</style>
    </div>
  );
}
